import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import Header from './Header';
import Footer from './Footer';
import { Mail, Lock, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { ForgotPasswordLink } from './ForgotPassword';
import { TermsAcceptModal } from './TermsAccept';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    senha: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingLogin, setPendingLogin] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.senha) {
      setError('Preencha todos os campos');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.senha
      });
      
      const { token, user } = response.data;
      if (!user.terms_accepted) { setPendingLogin({ token, user }); return; }
      finishLogin(token, user);
    } catch (err) {
      console.error('Erro no login:', err);
      setError(err.response?.data?.detail || 'E-mail ou senha inválidos');
    } finally {
      setLoading(false);
    }
  };

  const acceptTerms = async () => {
    const { token, user } = pendingLogin;
    await axios.post(`${API}/auth/accept-terms`, {}, { headers: { Authorization: `Bearer ${token}` } });
    finishLogin(token, { ...user, terms_accepted: true });
  };

  const finishLogin = (token, user) => {
      localStorage.setItem('token', token);
      localStorage.setItem('userData', JSON.stringify(user));
      
      // Redirecionar baseado no tipo de usuário
      if (user.role === 'superadmin') {
        navigate('/admin');
      } else {
        // Todos os usuários normais são editoras digitais; mantém link de e-mail (?next=/editor/dashboard?secao=...)
        const next = new URLSearchParams(window.location.search).get('next');
        navigate(next && next.startsWith('/editor/dashboard') ? next : '/editor/dashboard');
      }
  };

  return (
    <div className="login-page">
      <Header />
      
      <main className="login-main">
        <div className="login-container">
          <Card className="login-card">
            <CardHeader className="text-center">
              <CardTitle className="login-title">
                Entrar no <span className="text-accent">Livrum</span>
              </CardTitle>
              <p className="login-subtitle">Acesse sua conta de editora</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="login-form">
                <div className="form-field">
                  <Label htmlFor="email">E-mail</Label>
                  <div className="input-with-icon">
                    <Mail className="input-icon" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="seu@email.com"
                    />
                  </div>
                </div>

                <div className="form-field">
                  <Label htmlFor="senha">Senha</Label>
                  <div className="input-with-icon">
                    <Lock className="input-icon" />
                    <Input
                      id="senha"
                      name="senha"
                      type="password"
                      value={formData.senha}
                      onChange={handleChange}
                      placeholder="Sua senha"
                    />
                  </div>
                  <ForgotPasswordLink kind="staff" />
                </div>

                {error && (
                  <div className="login-error">
                    <AlertCircle className="w-4 h-4" />
                    <span>{error}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  className="btn-primary-custom w-full"
                  disabled={loading}
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Entrando...</>
                  ) : (
                    'Entrar'
                  )}
                </Button>
              </form>

              <div className="login-footer">
                <p>Ainda não tem uma conta?</p>
                <Button
                  variant="link"
                  className="link-button"
                  onClick={() => navigate('/cadastro')}
                >
                  Cadastre-se aqui
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      
      <Footer />
      {pendingLogin && <TermsAcceptModal onAccept={acceptTerms} onCancel={() => setPendingLogin(null)} />}
    </div>
  );
};

export default LoginPage;
